import { isValid } from "date-fns";
import moment from "moment";
import { type DependencyList, useEffect, useState } from "react";
import { useIsFirstRender } from "usehooks-ts";

import { showAPIErrorMessage } from "utilities/apiHandler";

export function useLoadEffect(
    loadFn: (handleLoadException: (e: unknown) => void) => void,
    deps?: DependencyList,
) {
    const isFirstRender = useIsFirstRender();
    const [, setState] = useState();

    const handleException = (e: unknown) => {
        if (isFirstRender) {
            setState(() => {
                throw e;
            });
        } else {
            showAPIErrorMessage(e);
        }
    };
    useEffect(() => {
        loadFn(handleException);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, deps);
}

/**
 * Returns a new Date object with the .toJSON method overridden to return a JSON string in local time.
 * This hack is necessary right now because apix handlers can only convert Date objects to UTC strings in request bodies.
 *
 * Returns a new Date object with the .toJSON method overridden.
 *
 * @param date - The date that will get converted to a JSON string
 * @returns A new Date object that will return a JSON string in local time
 *
 * @deprecated APIx handlers handle how to send dates to the server. This is no longer needed.
 */
export function forceDateJSONToLocalTime(date: Date | undefined) {
    if (!date) return undefined;
    const dateCopy = new Date(date);
    dateCopy.toJSON = () => moment(dateCopy).format("YYYY-MM-DDTHH:mm:ss");
    return dateCopy;
}

/**
 * This handles parsing a Date string from the server in the expected format that the server sends it in the builder's timezone.
 * @param dateStr - The date string to convert to a Date object
 * @returns The parsed Date object or undefined if the string is undefined, null, or in an invalid format.
 */
export function convertServerDateStringToDate(dateStr: string | undefined | null) {
    if (!dateStr) return undefined;
    const date = new Date(dateStr);
    if (!isValid(date)) return undefined;
    return date;
}
